import { fmtDate } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RequestChat } from "@/components/RequestChat";
import { toast } from "sonner";
import { RouteMap } from "@/components/RouteMap";
import { MapPin, ArrowRight, Calendar, Truck, Star, CheckCircle2, TrendingUp, Info } from "lucide-react";

function ScoreRing({ score }) {
  const color = score >= 85 ? "#10B981" : score >= 65 ? "#F59E0B" : "#94A3B8";
  return (
    <div className="relative w-16 h-16 shrink-0">
      <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#1E293B" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${score}, 100`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-head font-bold text-sm" style={{ color }}>{score}%</span>
      </div>
    </div>
  );
}

export default function Angebote() {
  const { requestId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const nav = useNavigate();
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      const [o, r] = await Promise.all([
        api.get(`/requests/${requestId}/offers`),
        api.get(`/requests/${requestId}`),
      ]);
      setOffers(o.data); setReq(r.data); setLoading(false);
    })();
  }, [requestId]);

  const select = async (offer) => {
    if (!user) { toast.error("Bitte zuerst anmelden, um zu buchen."); nav("/login"); return; }
    setBusy(offer.id);
    try {
      const { data } = await api.post("/bookings", { offer_id: offer.id });
      nav(`/checkout/${data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  if (loading) return <div className="eb-container py-16 text-slate-400">Suche passende Beiladungen…</div>;

  return (
    <div className="eb-container py-12 max-w-4xl">
      <span className="eb-label text-[#FF5A1F]">Regelbasiertes Matching</span>
      <h1 className="font-head text-3xl font-bold mt-2">Passende Beiladungen</h1>
      {req && <p className="text-slate-400 mt-2">{req.route_label} · {req.volume_m3} m³ · {req.cargo_type}{req.distance_km ? ` · ${req.distance_km} km` : ""}</p>}
      {req && <div className="mt-6"><RouteMap von={req.start?.ort} vonPlz={req.start?.plz} nach={req.dest?.ort} nachPlz={req.dest?.plz} /></div>}
      {req && (token || (user && user.id === req.customer_id)) && (
        <div className="eb-card p-5 mt-6" data-testid="request-contact-box">
          <div className="font-head font-semibold">Fragen zu deiner Anfrage?</div>
          <p className="text-sm text-slate-400 mt-1">Schreib uns direkt hier – wir antworten per E-Mail und die Unterhaltung bleibt dieser Anfrage zugeordnet.</p>
          <RequestChat requestId={requestId} token={token} me="customer" />
        </div>
      )}

      {offers.length === 0 ? (
        <div className="eb-card p-10 text-center mt-8">
          <p className="text-slate-300 mb-2">Aktuell keine passende Beiladung für diese Strecke.</p>
          <p className="text-sm text-slate-500 mb-5">Wir benachrichtigen dich, sobald eine passende Fahrt verfügbar ist.</p>
          <button onClick={() => nav("/aktuelle-beiladungen")} className="eb-btn-outline">Alle Beiladungen ansehen</button>
        </div>
      ) : (
        <div className="space-y-5 mt-8">
          {offers.map((o) => (
            <div key={o.id} className="eb-card eb-card-hover p-6" data-testid={`offer-card-${o.id}`}>
              <div className="flex items-start gap-4">
                <ScoreRing score={o.match_score} />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="eb-badge bg-emerald-500/15 text-emerald-400">{o.match_score}% passend</span>
                    <span className="text-xs text-slate-500 flex items-center gap-1" data-testid={`offer-partner-${o.id}`}><Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" /> {o.partner_name}
                      {o.partner_rating != null && (o.partner_review_count > 0
                        ? <span className="ml-1 text-amber-300" data-testid={`offer-rating-${o.id}`}>{Number(o.partner_rating).toFixed(1)} <span className="text-slate-500">({o.partner_review_count} {o.partner_review_count === 1 ? "Bewertung" : "Bewertungen"})</span></span>
                        : <span className="ml-1 text-slate-500" data-testid={`offer-rating-${o.id}`}>Neu – noch keine Bewertungen</span>)}
                    </span>
                    {o.owner_type === "admin" && <span className="eb-badge bg-[#FF5A1F]/15 text-[#FF8A5F]">OK Umzüge</span>}
                  </div>
                  <div className="flex items-center gap-2 font-head text-lg font-semibold">
                    <MapPin className="w-4 h-4 text-[#FF5A1F]" /> {o.route_label.split(" → ")[0]}
                    <ArrowRight className="w-4 h-4 text-slate-600" /> {o.route_label.split(" → ")[1]}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-400 mt-2">
                    <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {fmtDate(o.abfahrt)}</span>
                    <span className="flex items-center gap-1.5"><Truck className="w-4 h-4" /> {o.vehicle_type}</span>
                    <span className="flex items-center gap-1.5 mono text-emerald-400">{o.freie_m3} m³ frei</span>
                    {o.detour_km != null && <span className="flex items-center gap-1.5">Umweg {o.detour_km} km {o.distance_source === "google" && <span className="eb-badge bg-emerald-500/15 text-emerald-400">Google Maps</span>}</span>}
                    {o.via_segment && <span className="eb-badge bg-sky-500/15 text-sky-300" data-testid={`offer-segment-${o.id}`}>Teilstrecke {o.segment}{o.segment_factor && o.segment_factor < 1 ? ` · ${Math.round(o.segment_factor * 100)} % der Strecke` : ""}</span>}
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-lg bg-[#0B0F17] border border-slate-800 p-3 space-y-1 text-sm">
                {o.reasons?.map((r, i) => (
                  <div key={i} className="flex items-start gap-2 text-slate-400"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> {r}</div>
                ))}
              </div>

              <div className="mt-4 flex flex-col sm:flex-row sm:items-end justify-between gap-4 pt-4 border-t border-slate-800">
                <div className="text-sm">
                  <div className="flex items-center gap-1 text-xs text-slate-500 mb-1"><Info className="w-3 h-3" /> Transparente Preisaufschlüsselung</div>
                  <div className="text-slate-400">Partnerpreis <span className="mono text-slate-300">{o.partner_price} €</span> + Vermittlung <span className="mono text-slate-300">{o.commission} €</span></div>
                  <div className="font-head text-2xl font-bold text-[#FF5A1F] mt-1">{o.customer_price} € <span className="text-xs text-slate-500 font-normal">Kundenpreis</span></div>
                </div>
                <button onClick={() => select(o)} disabled={busy === o.id} className="eb-btn-primary" data-testid={`offer-select-${o.id}`}>
                  {busy === o.id ? "…" : "Angebot auswählen"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
